import React, { useEffect, useState, useRef, useCallback } from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { FiEye, FiImage, FiCheck } from "react-icons/fi";

import ClientCalling from "../../api/ClientCalling";
import SmartImage from "./SmartImage";
import { formatView } from "../formatView";

const PAGE_LIMIT = 24;

/* ---------- Skeleton ---------- */
const CardSkeleton = () => (
    <div className='relative aspect-[9/16] rounded-2xl ios-shimmer overflow-hidden'>
        <div
            className='absolute top-2 left-2 w-10 h-4 rounded-full'
            style={{ background: "rgba(255,255,255,0.08)" }}
        />
    </div>
);

/* ---------- Wallpaper card (click → opens the separate view page) ---------- */
export const WallpaperCard = ({ item, index = 0, onOpen }) => (
    <motion.div
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
            duration: 0.5,
            delay: Math.min(index * 0.025, 0.3),
            ease: [0.22, 1, 0.36, 1],
        }}
        whileHover={{ y: -5 }}
        whileTap={{ scale: 0.97 }}
        onClick={() => onOpen(item)}
        className='wp-card relative aspect-[9/16] rounded-2xl cursor-pointer group bg-client-back'
        style={{
            overflow: "clip",
            boxShadow:
                "0 10px 26px -10px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.06)",
        }}
    >
        <SmartImage
            src={item.w_thumbnail || item.w_image}
            alt={item.w_name}
            className='absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
        />
        <div
            className='absolute inset-x-0 bottom-0 h-3/5 pointer-events-none'
            style={{
                background:
                    "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 45%, transparent 100%)",
            }}
        />
        <div className='absolute top-2 left-2 ios-glass-dark px-2 py-0.5 rounded-full flex items-center gap-1 text-white text-[9px] md:text-[11px] font-medium pointer-events-none'>
            <FiEye className='text-[10px] md:text-xs' />
            <span className='tabular-nums'>{formatView(item.w_view_count || 0)}</span>
        </div>
        <div className='absolute inset-x-0 bottom-0 p-2 md:p-3'>
            <p
                className='text-white text-[10px] md:text-xs font-semibold truncate'
                style={{ textShadow: "0 1px 3px rgba(0,0,0,0.85)" }}
            >
                {item.w_name}
            </p>
            {item.w_category?.w_name && (
                <p className='text-white/60 text-[9px] md:text-[10px] truncate mt-0.5'>
                    {item.w_category.w_name}
                </p>
            )}
        </div>
        <div
            className='absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300'
            style={{
                boxShadow:
                    "inset 0 0 0 2px rgba(127,0,255,0.5), 0 0 28px rgba(127,0,255,0.35)",
            }}
        />
    </motion.div>
);

/* ---------- Feed ----------
   slug = ""  → main feed (all wallpapers)
   slug = "x" → that category's wallpapers
   Parent should pass key={slug} for category pages so it remounts cleanly. */
const WallpaperGrid = ({
    slug = "",
    search = "",
    onMeta,
    excludeSlug = "",
    emptyTitle = "No wallpapers yet",
    emptyText = "Check back soon for fresh wallpapers.",
}) => {
    const navigate = useNavigate();
    const observerTarget = useRef(null);
    const onMetaRef = useRef(onMeta);
    useEffect(() => {
        onMetaRef.current = onMeta;
    });

    const [listData, setListData] = useState([]);
    const [page, setPage] = useState(1);
    const [nextPage, setNextPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    const load = useCallback(
        async (pageNum) => {
            if (pageNum === 1) setLoading(true);
            else setLoadingMore(true);
            try {
                const opts = { sortBy: "createdAt", order: "desc" };
                if (search) opts.search = search;
                const resp = slug
                    ? await ClientCalling.getWallpaperByCategoryClient(
                          slug,
                          pageNum,
                          PAGE_LIMIT,
                          opts
                      )
                    : await ClientCalling.getWallpaperListClient(pageNum, PAGE_LIMIT, opts);

                if (resp?.status) {
                    if (pageNum === 1 && onMetaRef.current) onMetaRef.current(resp);
                    const items = Array.isArray(resp.data) ? resp.data : [];
                    setListData((prev) => (pageNum === 1 ? items : [...prev, ...items]));
                    setNextPage(resp.nextPage || null);
                } else if (pageNum === 1) {
                    setListData([]);
                    if (onMetaRef.current) onMetaRef.current(resp);
                }
            } catch (e) {
                if (pageNum === 1) setListData([]);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [slug, search]
    );

    useEffect(() => {
        load(page);
    }, [page, load]);

    /* infinite scroll */
    useEffect(() => {
        const el = observerTarget.current;
        if (!el || !nextPage) return;
        const obs = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && nextPage && !loading && !loadingMore) {
                    setPage(nextPage);
                }
            },
            { rootMargin: "400px", threshold: 0 }
        );
        obs.observe(el);
        return () => obs.unobserve(el);
    }, [nextPage, loading, loadingMore]);

    const openWallpaper = (item) => navigate(`/wallpaper/view/${item.w_slug}`);

    // Hide the current wallpaper from its own "More from" list
    const visible = excludeSlug
        ? listData.filter((w) => w.w_slug !== excludeSlug)
        : listData;

    return (
        <>
            {/* skeleton */}
            {loading && page === 1 && (
                <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4'>
                    {[...Array(12)].map((_, i) => (
                        <CardSkeleton key={i} />
                    ))}
                </div>
            )}

            {/* empty */}
            {!loading && visible.length === 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    className='flex flex-col items-center justify-center text-center py-16 md:py-24'
                >
                    <div
                        className='w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mb-5'
                        style={{
                            background:
                                "linear-gradient(135deg, rgba(127,0,255,0.25), rgba(252,250,2,0.08))",
                            border: "1px solid rgba(255,255,255,0.12)",
                        }}
                    >
                        <FiImage className='text-2xl md:text-3xl text-client-title' />
                    </div>
                    <h3 className='text-client-font font-bold text-lg md:text-2xl tracking-tight'>
                        {emptyTitle}
                    </h3>
                    <p className='text-client-font/55 text-sm md:text-base mt-2 max-w-md'>
                        {emptyText}
                    </p>
                </motion.div>
            )}

            {/* grid */}
            {visible.length > 0 && (
                <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4'>
                    {visible.map((item, idx) => (
                        <WallpaperCard
                            key={item._id}
                            item={item}
                            index={idx}
                            onOpen={openWallpaper}
                        />
                    ))}
                    {loadingMore &&
                        [...Array(6)].map((_, i) => <CardSkeleton key={`s-${i}`} />)}
                </div>
            )}

            {/* infinite scroll sentinel */}
            {nextPage && (
                <div
                    ref={observerTarget}
                    className='flex justify-center items-center py-8 md:py-12'
                >
                    {loadingMore && (
                        <div className='ios-glass-dark inline-flex items-center gap-3 px-5 py-3 rounded-full'>
                            <div className='w-4 h-4 rounded-full border-2 border-client-font/30 border-t-client-primary animate-spin' />
                            <span className='text-client-font text-xs md:text-sm font-medium tracking-wide'>
                                Loading more
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* end */}
            {!nextPage && !loading && visible.length > 0 && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className='flex flex-col items-center gap-2 py-10 text-client-font/40'
                >
                    <FiCheck className='text-2xl' style={{ color: "#7F00FF" }} />
                    <p className='text-xs md:text-sm tracking-wider uppercase'>
                        You've seen it all
                    </p>
                </motion.div>
            )}
        </>
    );
};

export default WallpaperGrid;
